import { Bot, Code2, TrendingUp, FlaskConical, Layers, Building2, Vote, Briefcase } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { MonoLabel } from "@/components/primitives";
import { ECOSYSTEM } from "@/lib/demo-data";
import { useSEO } from "@/lib/useSEO";
import { PageShell } from "@/components/PageShell";

const ICONS: Record<string, typeof Bot> = {
  "ai-agents": Bot,
  developers: Code2,
  traders: TrendingUp,
  researchers: FlaskConical,
  protocols: Layers,
  "autonomous-businesses": Building2,
  daos: Vote,
  "service-providers": Briefcase,
};

export default function Ecosystem() {
  useSEO({
    title: "Ecosystem — Who Builds on Consequence | Consequence Protocol",
    description:
      "AI agents, developers, traders, researchers, protocols, autonomous businesses, DAOs, and service providers — what each can prove on the Consequence Layer.",
    path: "/ecosystem",
  });

  return (
    <PageShell testId="page-ecosystem">
      <section className="container-t pb-16 pt-20 sm:pt-28">
        <SectionHeader
          index="00"
          label="Ecosystem"
          title={
            <>
              Who needs a <span className="text-cyan">track record</span>
            </>
          }
          description="The future economy will contain humans, agents, and autonomous systems working side by side. Every one of them needs the same thing: a verifiable record of what their actions achieved."
        />
      </section>

      <section className="container-t pb-24" data-testid="ecosystem-grid">
        <div className="grid gap-5 md:grid-cols-2">
          {ECOSYSTEM.map((e, i) => {
            const Icon = ICONS[e.id];
            return (
              <Reveal key={e.id} delay={(i % 2) * 0.08}>
                <article className="panel panel-hover hud-corners h-full p-6 sm:p-8" data-testid={`ecosystem-card-${e.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center border border-line text-cyan">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="font-display text-lg font-bold uppercase tracking-wide text-ink">{e.name}</span>
                    </span>
                    <MonoLabel className="text-[8px] text-dim/60">ECO.{String(i + 1).padStart(2, "0")}</MonoLabel>
                  </div>
                  <div className="mt-6 space-y-4">
                    {[
                      ["CAN PROVE", e.prove],
                      ["CONSEQUENCE MEASURED", e.measure],
                      ["REPUTATION", e.reputation],
                    ].map(([k, v]) => (
                      <div key={k} className="border-b border-line/60 pb-4 last:border-0 last:pb-0">
                        <MonoLabel className="text-[8px] text-cyan">{k}</MonoLabel>
                        <p className="mt-1.5 text-sm leading-relaxed text-dim">{v}</p>
                      </div>
                    ))}
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>
    </PageShell>
  );
}
